import {ChangeDetectionStrategy, Component, DestroyRef, DOCUMENT, inject, Inject, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Platform} from '@angular/cdk/platform';
import {TranslateService} from '@ngx-translate/core';
import {interval, of, Subscription, timer} from 'rxjs';
import {filter, map, switchMap, take} from 'rxjs/operators';
import {SwUpdate, VersionReadyEvent} from '@angular/service-worker';
import {MatSnackBar} from '@angular/material/snack-bar';

import {FuseConfigService} from '@fuse/services/config.service';
import {FuseNavigationService} from '@fuse/components/navigation/navigation.service';
import {FuseSidebarService} from '@fuse/components/sidebar/sidebar.service';
import {FuseSplashScreenService} from '@fuse/services/splash-screen.service';
import {FuseTranslationLoaderService} from '@fuse/services/translation-loader.service';

import {navigation} from 'app/navigation/navigation';
import {locale as navigationEnglish} from 'app/navigation/i18n/en';
import {AuthService} from './kluh-manager/auth.service';
import {isWindowsOS} from './kluh-manager/helpers/kluh';
import {UserAuthorityDaoService} from './kluh-manager/main/user-authority/user-authority-dao.service';
import {CustomerGroup, FirebaseUserDetails} from './kluh-manager/models';
import {DesktopServerService} from './kluh-manager/main/desktop-server/desktop-server.service';
import {NavigationEnd, Router} from '@angular/router';
import {ManagerUserAccessPeriodProfileDaoService} from './kluh-manager/main/manager-user-access-period/manager-user-access-period-profile-dao.service';
import {MarketplaceOrderService} from './kluh-manager/main/marketplace/marketplace-order/marketplace-order.service';
import {USER_TOPIC} from '../main';
import {R2CloudStompService} from './kluh-manager/ws/r2-cloud-stomp.service';
import {ValueWrapper} from './kluh-manager/helpers/value-wrapper';
import {R2MenuService} from './kluh-manager/utils/r2-menu.service';
import {CustomerGroupService} from './kluh-manager/main/customer-group/customer-group.service';
import {CustomerGroupDaoService} from './kluh-manager/main/customer-group/customer-group-dao.service';
import {IdleService} from './kluh-manager/utils/idle.service';
import {OpenServerErrorService} from './kluh-manager/main/ad-admin/open-server-error.service';

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
    fuseConfig: any;
    navigation: any;

    private readonly destroyRef = inject(DestroyRef);
    private loginTouched = false;
    private timerCheckAccessDenied!: ReturnType<typeof setInterval>;
    private idleSub!: Subscription;

    // private timerCheckMarketplaceOrder: NodeJS.Timer;

    /**
     * Constructor
     *
     * @param {DOCUMENT} document
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseNavigationService} _fuseNavigationService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {FuseSplashScreenService} _fuseSplashScreenService
     * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
     * @param {Platform} _platform
     * @param {TranslateService} _translateService
     */
    constructor(
        @Inject(DOCUMENT) private document: any,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _fuseSplashScreenService: FuseSplashScreenService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _translateService: TranslateService,
        private _platform: Platform,
        private authService: AuthService,
        private r2MenuService: R2MenuService,
        private userAuthorityDao: UserAuthorityDaoService,
        private desktopServerService: DesktopServerService,
        private router: Router,
        private managerUserAccessPeriodProfileDaoService: ManagerUserAccessPeriodProfileDaoService,
        private r2CloudStompService: R2CloudStompService,
        private marketplaceOrderService: MarketplaceOrderService,
        private customerGroupService: CustomerGroupService,
        private customerGroupDaoService: CustomerGroupDaoService,
        private idleService: IdleService,
        private openServerErrorService: OpenServerErrorService,
        private swUpdate: SwUpdate,
        private snackBar: MatSnackBar
    ) {
        // Get default navigation
        this.navigation = navigation;
        this.router.events.pipe(
            takeUntilDestroyed(this.destroyRef),
            filter((event): event is NavigationEnd => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            if (event.url.startsWith('/login')) {
                this.loginTouched = true;
            }
        });

        this._fuseNavigationService.register('main', this.navigation);

        this.authService.isLoggedIn().pipe(switchMap((value: boolean) => {
            if (value) {
                return this.userAuthorityDao.getMe().pipe(take(1), map((result) => {
                    this.scheduleCustomerGroupAlert(8000);
                    this.scheduleIdleLogoutTimeout(8000);
                    return [result, value] as [FirebaseUserDetails | null, boolean];
                }));
            } else {
                return of([null, value] as [FirebaseUserDetails | null, boolean]);
            }
        })).subscribe((result) => {
            const me = result[0];
            const isLoggedIn = result[1];
            if (me && isLoggedIn) {
                this.r2CloudStompService.stompTopic<ValueWrapper>(`${USER_TOPIC}/web-quit`).subscribe((resultStompTopic) => {
                    this.authService.forcedLogout(resultStompTopic.value);
                });
                // R2CLOUD-1259 — a recusa de "Abrir com Usuário do AD" chega em `/user/topic/open-server-error`
                // e vira modal. Fica aqui, e não na tela Desktop, porque o mesmo pedido também parte do painel
                // de administração de projetos — e porque o dialog que originou o pedido já foi fechado.
                this.openServerErrorService.start();
                this.checkAccessDenied();
                this.timerCheckAccessDenied = setInterval(() => {
                    this.checkAccessDenied();
                }, 300000);

                if (this.loginTouched) {
                    setTimeout(() => {
                        this.setNavBarAndToolBarHidden(false);
                    }, 500);
                } else {
                    this.setNavBarAndToolBarHidden(false);
                }
                this.desktopServerService.initGlobal();

                this.marketplaceOrderService.getAmountOfUnreadOrdersAndPutOnMenuBadge();

                this.hiddenDesktopMenuIfIsNotWindowsInterval();
            } else {

                if (this.timerCheckAccessDenied) {
                    clearInterval(this.timerCheckAccessDenied);
                }
                if (this.timerCheckAccessDenied) {
                    clearInterval(this.timerCheckAccessDenied);
                }

                this.desktopServerService.ngOnDestroy();

                this.setNavBarAndToolBarHidden(true);

                if (isLoggedIn) {
                    this.authService.signOut().subscribe();
                }
            }
        });

        this._fuseNavigationService.onNavigationChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((menu) => {
            if (menu) {
                this.r2MenuService.innitVisibleMenu();
            }
        });

        if (this.swUpdate.isEnabled) {
            this.swUpdate.versionUpdates.pipe(
                filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(() => {
                const snackBarRef = this.snackBar.open(
                    'Nova versao disponivel.',
                    'Atualizar',
                    {duration: 0}
                );
                snackBarRef.onAction().subscribe(() => {
                    this.swUpdate.activateUpdate().then(() => document.location.reload());
                });
            });

            interval(30 * 60 * 1000).pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(() => this.swUpdate.checkForUpdate());
        }

        // Add languages
        this._translateService.addLangs(['en', 'pt-br']);

        // Set the default language
        this._translateService.setDefaultLang('en');

        // Set the navigation translations
        this._fuseTranslationLoaderService.loadTranslations(navigationEnglish);

        // Use a language
        this._translateService.use('pt-br');

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix Start
         * ----------------------------------------------------------------------------------------------------
         */

        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         **/

        // Set the default language to 'en' and then back to 'tr'.
        // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
        // been selected and there is no way to force it, so we overcome the issue by switching
        // the default language back and forth.
        /**
         setTimeout(() => {
         this._translateService.setDefaultLang('en');
         this._translateService.setDefaultLang('tr');
         });
         */

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix End
         * ----------------------------------------------------------------------------------------------------
         */

        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }
    }

    private hiddenDesktopMenuIfIsNotWindowsInterval(): void {
        let count = 0;
        this.hiddenDesktopMenuIfIsNotWindows();
        const intervalId = setInterval(() => {
            this.hiddenDesktopMenuIfIsNotWindows();
            count++;
            if (count >= 20) {
                clearInterval(intervalId);
            }
        }, 250);
    }

    private hiddenDesktopMenuIfIsNotWindows(): void {
        if (!isWindowsOS()) {
            this._fuseNavigationService.updateNavigationItem('desktop', {
                hidden: true
            });
        }
    }

    private scheduleCustomerGroupAlert(delayMs: number): void {
        timer(delayMs).pipe(
            switchMap(() => this.customerGroupService.get()),
            filter((g): g is CustomerGroup => g != null),
            switchMap(g => this.customerGroupDaoService.getOne(g.id ?? 0))
        ).subscribe(
            cg => this.customerGroupService.showCustomerGroupAlertMessage(cg),
            err => console.error('Error fetching customer group:', err)
        );
    }

    private scheduleIdleLogoutTimeout(delayMs: number): void {
        timer(delayMs).pipe(
            switchMap(() => this.customerGroupDaoService.getByAuthenticatedManagerUser())
        ).subscribe(
            cg => this.configureIdleLogout(cg),
            err => console.error('Error fetching customer group:', err)
        );
    }

    private configureIdleLogout(cg: CustomerGroup): void {
        if (!cg.enableIdleLogout) {
            return;
        }
        const idleTimeMs = (cg.idleLogoutTimeoutMin ?? 0) * 60_000;
        const idleTimeMinutes = idleTimeMs / 60_000;
        this.idleService.startWatching(idleTimeMs);
        console.log('watching1: starting watching user: ' + idleTimeMinutes + ' minutos');
        this.idleSub?.unsubscribe();
        this.idleSub = this.idleService.onIdle$.subscribe(() => {
            console.log(`watching2: Usuário inativo há ${idleTimeMinutes} minutos`);
            this.authService.forcedLogout(
                `<br>Desconectado por inatividade.<br><br>` +
                `<br>Tempo de inatividade: ${idleTimeMinutes} minutos<br><br>`
            );
        });
    }

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to config changes
        this._fuseConfigService.config
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((config: any) => {

                this.fuseConfig = config;

                // Boxed
                if (this.fuseConfig.layout.width === 'boxed') {
                    this.document.body.classList.add('boxed');
                } else {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme
                if (this.document && this.document.body.classList) {
                    for (let i = 0; i < this.document.body.classList.length; i++) {
                        const className = this.document.body.classList[i];

                        if (className.startsWith('theme-')) {
                            this.document.body.classList.remove(className);
                        }
                    }
                }

                this.document.body.classList.add(this.fuseConfig.colorTheme);
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        clearInterval(this.timerCheckAccessDenied);
        this.customerGroupService.destroyer();
        this.idleSub?.unsubscribe();
        this.idleService.stopWatching();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key: string): void {
        this._fuseSidebarService.getSidebar(key)?.toggleOpen();
    }

    private setNavBarAndToolBarHidden(value: boolean): void {
        this._fuseConfigService.setConfig({
            layout: {
                navbar: {
                    hidden: value
                },
                toolbar: {
                    hidden: value
                }
            }
        });
    }

    private checkAccessDenied(): void {
        this.managerUserAccessPeriodProfileDaoService.canAccess().subscribe((canAccess) => {
            if (canAccess) {
                this.authService.processCanAccess(canAccess);
                // console.debug('canAccess: ' + canAccess[0] + ' | ' + canAccess[1] + ' | ' + canAccess[2]);
            }
        });
    }
}
